import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Building2, Briefcase, Check, Plus, ArrowRight, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import AppLayout from '@/components/app/AppLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Service options for existing company
const SERVICES = [
  { 
    id: 'ein_application', 
    name: 'EIN (Tax ID)', 
    price: '$99',
    description: 'Federal Employer Identification Number'
  },
  { 
    id: 'registered_agent', 
    name: 'Registered Agent', 
    price: '$150/year',
    description: 'Legal document receiving service'
  },
  { 
    id: 'mail_forwarding', 
    name: 'Mail Forwarding', 
    price: '$200/year',
    description: 'Professional US business address'
  },
  { 
    id: 'amendment', 
    name: 'State Filings & Amendments', 
    price: '$200+',
    description: 'Update company details, name changes'
  },
];

const GetStartedPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedServices, setSelectedServices] = useState([]);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const res = await axios.get(`${API}/companies/${companyId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCompany(res.data);
      } catch (err) {
        console.error('Error fetching company:', err);
      } finally {
        setLoading(false);
      }
    };
    
    if (token && companyId) fetchCompany();
  }, [token, companyId]);

  const toggleService = (serviceId) => {
    setSelectedServices(prev => 
      prev.includes(serviceId) 
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const handleFormationPackage = () => {
    navigate(`/dashboard/companies/${companyId}/formation-package`);
  };

  const handleExistingCompanyServices = () => {
    if (selectedServices.length === 0) return;
    
    // Navigate to new order page with first selected service and company pre-selected
    const firstService = selectedServices[0];
    const remainingServices = selectedServices.slice(1);
    
    // Store remaining services in sessionStorage to process after first order
    if (remainingServices.length > 0) {
      sessionStorage.setItem('pendingServices', JSON.stringify({
        services: remainingServices,
        companyId: companyId
      }));
    }
    
    navigate(`/dashboard/orders/new?service=${firstService}&company=${companyId}`);
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#059669] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      {/* Back Link */}
      <Link 
        to="/dashboard/companies" 
        className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Companies
      </Link>

      {/* Header */}
      <div className="text-center mb-10">
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
          {company?.company_name || 'Your Company'} is ready
        </h1>
        <p className="text-gray-400 text-lg">
          What would you like to do next?
        </p>
      </div>

      {/* Journey Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl mx-auto">
        
        {/* Card 1 - Form a New Company */}
        <div className="bg-[#111827] border border-gray-800 rounded-xl p-6 flex flex-col">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-[#059669]/10 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6 text-[#059669]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Form My Company</h2>
            </div>
          </div>
          
          <p className="text-gray-400 text-sm mb-6">
            I want to officially register my company with the state, get a Tax ID (EIN), 
            and set up a Registered Agent. Everything you need to legally operate in the US.
          </p>
          
          <div className="space-y-3 mb-6 flex-1">
            <p className="text-gray-500 text-xs uppercase font-medium tracking-wide">What's included</p>
            
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 text-[#059669] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white font-medium">Company Formation (LLC/C-Corp/S-Corp)</p>
                <p className="text-gray-500 text-sm">Official state registration</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 text-[#059669] flex-shrink-0 mt-0.5" />
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-white font-medium">EIN (Tax ID)</p>
                  <span className="px-2 py-0.5 bg-[#059669]/20 text-[#059669] text-xs rounded-full">Required</span>
                </div>
                <p className="text-gray-500 text-sm">Required by IRS to open bank accounts, hire employees, and file taxes</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 text-[#059669] flex-shrink-0 mt-0.5" />
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-white font-medium">Registered Agent</p>
                  <span className="px-2 py-0.5 bg-[#059669]/20 text-[#059669] text-xs rounded-full">Required</span>
                </div>
                <p className="text-gray-500 text-sm">Required by law in every US state to receive legal documents on your behalf</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <Plus className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-gray-300 font-medium">Mail Forwarding</p>
                  <span className="px-2 py-0.5 bg-gray-700 text-gray-400 text-xs rounded-full">Optional</span>
                </div>
                <p className="text-gray-500 text-sm">Professional US business address</p>
              </div>
            </div>
          </div>
          
          <button
            onClick={handleFormationPackage}
            className="w-full py-3 px-4 bg-[#059669] hover:bg-[#00D46E] text-black font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors"
            data-testid="start-formation-btn"
          >
            Start Formation Package
            <ArrowRight className="w-5 h-5" />
          </button>
          
          <Link
            to={`/dashboard/companies/${companyId}`}
            className="block text-center text-gray-500 hover:text-gray-400 text-sm mt-4 transition-colors"
          >
            Skip for now
          </Link>
        </div>

        {/* Card 2 - Existing Company Services */}
        <div className="bg-[#111827] border border-gray-800 rounded-xl p-6 flex flex-col">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center">
              <Briefcase className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Get Services for Existing Company</h2>
            </div>
          </div>
          
          <p className="text-gray-400 text-sm mb-6">
            My company is already formed. I need specific services like EIN, 
            Registered Agent, Mail Forwarding, or state filings.
          </p>
          
          <div className="space-y-3 mb-6 flex-1">
            <p className="text-gray-500 text-xs uppercase font-medium tracking-wide">Select services</p>
            
            {SERVICES.map((service) => (
              <button
                key={service.id}
                onClick={() => toggleService(service.id)}
                className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                  selectedServices.includes(service.id)
                    ? 'border-[#059669] bg-[#059669]/10'
                    : 'border-gray-700 bg-[#0b1310] hover:border-gray-600'
                }`}
                data-testid={`service-${service.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      selectedServices.includes(service.id)
                        ? 'border-[#059669] bg-[#059669]'
                        : 'border-gray-600'
                    }`}>
                      {selectedServices.includes(service.id) && (
                        <Check className="w-3 h-3 text-black" />
                      )}
                    </div>
                    <div>
                      <p className="text-white font-medium">{service.name}</p>
                      <p className="text-gray-500 text-sm">{service.description}</p>
                    </div>
                  </div>
                  <span className="text-[#059669] font-semibold">{service.price}</span>
                </div>
              </button>
            ))}
          </div>
          
          <button
            onClick={handleExistingCompanyServices}
            disabled={selectedServices.length === 0}
            className="w-full py-3 px-4 bg-[#059669] hover:bg-[#00D46E] text-black font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-600"
            data-testid="continue-services-btn"
          >
            Continue with Selected Services
            <ArrowRight className="w-5 h-5" />
          </button>
          
          <Link
            to={`/dashboard/companies/${companyId}`}
            className="block text-center text-gray-500 hover:text-gray-400 text-sm mt-4 transition-colors"
          >
            Skip for now
          </Link>
        </div>
      </div>
    </AppLayout>
  );
};

export default GetStartedPage;
